import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import twitchLogo from './images/twitchLogoSquare.png';
import youtubeLogo from './images/youtubeLogoSquare.png';
import kickLogo from './images/kickLogoSquare.png';
import tiktokLogo from './images/tiktokLogoSquare.png';
import starviosLogo from './images/starviosLogoSquare.png';

// Chat unificado de Twitch, YouTube, Kick y Starvios para moderar desde un solo sitio (apps/chat.js):
//   https://forallia.com/?streamGadgets=chat
// Solo con sesion de admin o, en OBS (que no la comparte con el navegador), con la clave en la
// URL: &key=<chatKey de apps/var.js>. Sin una de las dos el servidor no manda ni un mensaje.
// Para enseñarlo en el directo: &overlay=1 (sin cabecera, hora, botones ni baneados y con fondo
// transparente), &bg=0.5 para un fondo negro con esa opacidad y &color=ffcc00 para el texto.
// Con <Chat activity /> es el panel de actividad (?streamGadgets=actividad): follows, subs,
// donaciones, raids y canjes de todas las plataformas, con la misma clave y las mismas opciones.

const query = new URLSearchParams(window.location.search);
const chatKey = query.get('key') || '';
const overlay = query.get('overlay') === '1';
const overlayColor = query.get('color') || '';
const overlayStyle = overlay ? {
    background: 'rgba(0, 0, 0, ' + (parseFloat(query.get('bg')) || 0) + ')',
    // En hexadecimal sin "#" (en la URL habria que escribirlo %23); vale tambien un nombre: red.
    color: /^[0-9a-f]{3,8}$/i.test(overlayColor) ? '#' + overlayColor : overlayColor
} : undefined;
const MAX_MESSAGES = 500;
// La misma clave que usa el servidor para un ban: en YouTube cada canal banea por separado.
const keyOf = (who) => who.platform + ':' + who.account + ':' + who.userId;
const clock = (date) => new Date(date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

const PLATFORMS = {
    twitch: [twitchLogo, 'Twitch'],
    youtube: [youtubeLogo, 'YouTube'],
    kick: [kickLogo, 'Kick'],
    tiktok: [tiktokLogo, 'TikTok'],
    starvios: [starviosLogo, 'Starvios']
};

const PlatformIcon = ({ who }) => {
    const [src, name] = PLATFORMS[who.platform] || PLATFORMS.twitch;
    // En YouTube hay varios canales: cual.
    const label = (name + ' ' + (who.account || '')).trim();
    return <img className='chatIcon' src={src} alt={label} title={label} />;
};

const Chat = ({ activity }) => {
    const [messages, setMessages] = useState([]);
    const [bans, setBans] = useState([]);
    const [pinned, setPinned] = useState(null);
    // El mensaje con los botones abiertos: salen al hacer clic en el nombre, no al pasar por encima.
    const [open, setOpen] = useState(null);
    const [showBans, setShowBans] = useState(false);
    const [loggedOut, setLoggedOut] = useState(false);
    const [error, setError] = useState('');
    // Solo para repintar: un ban temporal caduca sin que llegue ningun evento.
    const [, setNow] = useState(0);
    const list = useRef(null);
    // La lista baja con cada mensaje salvo que se marque "Quitar scroll automático" (para revisar
    // algo a mano) o haya botones abiertos: si las filas se movieran bajo el cursor se acabaria
    // baneando al de al lado. Antes se paraba sola al subir a leer, pero mirando la posicion del
    // scroll fallaba a ratos.
    const [autoScroll, setAutoScroll] = useState(true);
    // Al banear, donde estaba "Ban" pasa a estar "Desbanear" (y al reves): el segundo clic de
    // un doble clic haria justo lo contrario. Tras un clic o un cambio se ignoran un momento.
    const lastChange = useRef(0);

    const toBottom = () => {
        if (list.current && autoScroll && open === null) list.current.scrollTop = list.current.scrollHeight;
    };

    useEffect(() => {
        // Aqui dentro y no arriba del fichero: App.js importa todas las vistas y abriria un
        // socket por cada visitante de la web.
        const socket = io({ auth: { key: chatKey } });
        // Al abrir (aunque el socket no conecte, que se vea si falta la sesion) y tras cada
        // conexion, por lo que se haya perdido mientras tanto.
        const feed = activity ? 'activity' : 'messages';
        const load = () => fetch('/api/chat', { credentials: 'include', headers: { 'X-Chat-Key': chatKey } })
            .then(res => {
                setLoggedOut(res.status === 401);
                return res.json();
            })
            .then(data => {
                if (!data[feed]) return;
                setBans(data.bans);
                setPinned(data.pinned);
                setMessages(prev => {
                    const ids = new Set(data[feed].map(m => m.id));
                    return [...prev.filter(m => !ids.has(m.id)), ...data[feed]].sort((a, b) => a.ts - b.ts);
                });
            })
            .catch(() => setError('No se pudo cargar el chat.'));
        load();
        socket.on('connect', load);
        socket.on(activity ? 'activity' : 'chatMessage', message => setMessages(prev =>
            prev.some(m => m.id === message.id) ? prev : [...prev, message].slice(-MAX_MESSAGES)));
        socket.on('chatBans', active => {
            lastChange.current = Date.now();
            setBans(active);
        });
        // Tambien mueve las filas (el fijado va encima de la lista): mismo respiro que un ban.
        socket.on('chatPinned', message => {
            lastChange.current = Date.now();
            setPinned(message);
        });
        const tick = setInterval(() => setNow(Date.now()), 15000);
        return () => {
            socket.close();
            clearInterval(tick);
        };
    }, [activity]);

    // Tambien al cerrar los botones o volver a activarlo, por lo que haya llegado mientras tanto.
    useEffect(toBottom, [messages, open, autoScroll]);

    // html tiene color de fondo propio en App.css: en OBS taparia la escena.
    useEffect(() => {
        if (overlay) document.documentElement.style.backgroundColor = 'transparent';
    }, []);

    // El cambio vuelve por el socket ('chatBans'), a esta ventana y a cualquier otra abierta.
    const moderate = (action, who, minutes) => {
        if (Date.now() - lastChange.current < 700) return;
        lastChange.current = Date.now();
        setError('');
        setOpen(null);
        fetch('/api/chat/' + action, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', 'X-Chat-Key': chatKey },
            body: JSON.stringify({ id: who.id, platform: who.platform, account: who.account, userId: who.userId, name: who.name, minutes: minutes })
        }).then(res => res.json())
            .then(data => { if (data.error) setError(data.error); })
            .catch(() => setError('No se pudo conectar con el servidor.'));
    };

    if (loggedOut) {
        return (
            <div className='chatPage'>
                {/* Sin enlace al login a proposito: esta URL la puede abrir cualquiera. */}
                <div className='rewardTimerKeyWarn'>
                    {activity ? 'El panel de actividad' : 'El chat unificado'} es solo para administradores.{chatKey ? ' La clave de la URL no vale.' : ''}
                </div>
            </div>
        );
    }

    const active = bans.filter(ban => !ban.until || new Date(ban.until) > Date.now());
    const banned = new Set(active.map(keyOf));
    const actions = (who) => banned.has(keyOf(who))
        ? <button className='copyRewardBtn' onClick={() => moderate('unban', who)}>Desbanear</button>
        : <>
            <button className='copyRewardBtn' onClick={() => moderate('ban', who, 10)}>10 min</button>
            <button className='copyRewardBtn chatBanForever' onClick={() => moderate('ban', who, 0)}>Ban</button>
        </>;

    return (
        <div className={'chatPage' + (overlay ? ' chatOverlay' : '')} style={overlayStyle}>
            {overlay ? '' : (
                <div className='chatHeader'>
                    <span>{activity ? 'Actividad' : 'Chat unificado'}</span>
                    <span>
                        <label className='chatAutoScroll'>
                            <input type='checkbox' checked={!autoScroll} onChange={e => setAutoScroll(!e.target.checked)} /> Quitar scroll automático
                        </label>
                        {activity ? '' : <button className='copyRewardBtn' onClick={() => setShowBans(!showBans)}>Baneados ({active.length})</button>}
                    </span>
                </div>
            )}
            {showBans ? (
                <div className='chatBans'>
                    {active.length === 0 ? <div className='chatRow'>No hay nadie baneado.</div> : ''}
                    {active.map(ban => (
                        <div className='chatRow' key={ban._id}>
                            <PlatformIcon who={ban} />
                            <span className='chatName'>{ban.name}</span>
                            <span className='chatText'>{ban.until ? 'hasta las ' + clock(ban.until) : 'para siempre'}</span>
                            <span className='chatActions'>{actions(ban)}</span>
                        </div>
                    ))}
                </div>
            ) : ''}
            {/* Tambien en el overlay: para enseñarlo en el directo. */}
            {pinned && !activity ? (
                <div className='chatRow chatPinned'>
                    <PlatformIcon who={pinned} />
                    <span className='chatName'>{pinned.name}</span>
                    <span className='chatText'>{pinned.text}</span>
                    {overlay ? '' : <span className='chatActions'><button className='copyRewardBtn' onClick={() => moderate('unpin', pinned)}>Desfijar</button></span>}
                </div>
            ) : ''}
            <div className='chatList' ref={list}>
                {messages.map(message => (
                    <div className={'chatRow' + (activity ? ' chatKind-' + message.kind : message.note ? ' chatHighlight' : '') + (banned.has(keyOf(message)) ? ' chatBanned' : '')} key={message.id}>
                        <PlatformIcon who={message} />
                        <span className='chatTime'>{clock(message.ts)}</span>
                        {/* Destacados de Twitch: mensaje destacado, bits, racha o sub. */}
                        {message.note ? <span className='chatNote'>{message.note}</span> : ''}
                        {overlay || activity ? <span className='chatName'>{message.name}</span>
                            : <button className='chatName' onClick={() => setOpen(open === message.id ? null : message.id)}>{message.name}</button>}
                        <span className='chatText'>{message.text}</span>
                        {open === message.id ? (
                            <span className='chatActions'>
                                {pinned && pinned.id === message.id
                                    ? <button className='copyRewardBtn' onClick={() => moderate('unpin', message)}>Desfijar</button>
                                    : <button className='copyRewardBtn' onClick={() => moderate('pin', message)}>Fijar</button>}
                                {/* Ni al streamer ni al bot: las plataformas tampoco dejan. */}
                                {message.staff || !message.userId ? '' : actions(message)}
                            </span>
                        ) : ''}
                    </div>
                ))}
            </div>
            {/* Debajo de la lista: arriba empujaria las filas y el siguiente clic caeria en otro usuario. */}
            {error ? <div className='rewardTimerKeyWarn' onClick={() => setError('')}>{error}</div> : ''}
        </div>
    );
};

export default Chat;
